import React, { useContext, useEffect } from "react";
import { ProspectFiltersType } from "../containers/Prospects";
import { ScheduleClubGameGridFiltersDTO, ScheduleGameAdminListFiltersDTO } from "../types/Schedule";
import { useMetadata } from "./MetadataContext";

/**
 * To add a new saved search:
 * If it is a new page:
 *   1. Create a new SearchKey object for the page
 *   2. Add a new key to SavedSearchPage object for the page
 *   3. create a new filter definition for the page
 *   4. add a new mapping to the SavedSearchPages object for the page
 * If it is an existing page:
 *   1. Add a new key to the corresponding SearchKey object for the page
 */

// saved searches per page
export const ProspectGridSearchKey = {
  CurrentYearRegistered: "currentYearRegistered",
  ProfileCompletedLast24Hrs: "profileCompletedLast24Hrs",
  GeneralQuestionnaireLast24Hrs: "generalQuestionnaireLast24Hrs",
  MedicalQuestionnaireLast24Hrs: "medicalQuestionnaireLast24Hrs",
  ClubQuestionnaireLast24Hrs: "clubQuestionnaireLast24Hrs",
  PreviousSearch: "previousSearch",
} as const;

export const AdminGameGridSearchKey = {
  PreviousSearch: "previousSearch",
} as const;

export const ClubGameGridSearchKey = {
  PreviousSearch: "previousSearch",
} as const;

// list of pages
export const SavedSearchPage = {
  ProspectsGrid: "prospectsGrid",
  AdminGameGrid: "adminGameGrid",
  ClubGameGrid: "clubGameGrid",
} as const;

type ValuesOf<T> = T[keyof T];

// filter definitions for each page
export type ProspectGridSavedSearches = Record<ValuesOf<typeof ProspectGridSearchKey>, Partial<ProspectFiltersType>>;

export type AdminGameGridSavedSearches = Record<
  ValuesOf<typeof AdminGameGridSearchKey>,
  Partial<ScheduleGameAdminListFiltersDTO>
>;

export type ClubGameGridSavedSearches = Record<
  ValuesOf<typeof ClubGameGridSearchKey>,
  Partial<ScheduleClubGameGridFiltersDTO>
>;

// mapping of pages and their filter definitions
export type SavedSearchPages = {
  [SavedSearchPage.ProspectsGrid]: ProspectGridSavedSearches;
  [SavedSearchPage.AdminGameGrid]: AdminGameGridSavedSearches;
  [SavedSearchPage.ClubGameGrid]: ClubGameGridSavedSearches;
};

type SavedSearchKeysFor<P extends keyof SavedSearchPages> = keyof SavedSearchPages[P] & string;
type SavedSearchValueFor<P extends keyof SavedSearchPages> = SavedSearchPages[P][keyof SavedSearchPages[P]];

type SavedSearchContextType = {
  saveSearch: <P extends keyof SavedSearchPages>(
    page: P,
    searchKey: SavedSearchKeysFor<P>,
    searchValue: string,
  ) => void;
  getSearch: <P extends keyof SavedSearchPages>(
    page: P,
    searchKey: SavedSearchKeysFor<P>,
  ) => SavedSearchValueFor<P> | null;
};

const SavedSearchContext = React.createContext<SavedSearchContextType | undefined>(undefined);

const SavedSearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { draftYears } = useMetadata();
  const draftYear: string = !!draftYears?.currentDraftYear ? draftYears?.currentDraftYear?.toString() : "";

  const saveSearch = <P extends keyof SavedSearchPages>(
    page: P,
    searchKey: SavedSearchKeysFor<P>,
    searchValue: string,
  ) => {
    const existingSearchesStr = localStorage.getItem("savedSearches");
    if (existingSearchesStr === null) {
      localStorage.setItem("savedSearches", JSON.stringify({ [page]: { [searchKey]: searchValue } }));
    } else {
      const existingSearches = JSON.parse(existingSearchesStr);
      localStorage.setItem(
        "savedSearches",
        JSON.stringify({
          ...existingSearches,
          [page]: { ...existingSearches[page], [searchKey]: searchValue },
        }),
      );
    }
  };

  const saveProspectGridSearches = async (year: string) => {
    saveSearch(
      SavedSearchPage.ProspectsGrid,
      ProspectGridSearchKey.CurrentYearRegistered,
      JSON.stringify({
        draftYears: [{ value: parseInt(year), label: year }],
        invitationStatuses: [
          {
            value: {
              value: "registered",
              name: "Registered",
            },
            label: "Registered",
          },
        ],
      } as Partial<ProspectFiltersType>),
    );
    saveSearch(
      SavedSearchPage.ProspectsGrid,
      ProspectGridSearchKey.ProfileCompletedLast24Hrs,
      JSON.stringify({
        draftYears: [{ value: parseInt(year), label: year }],
        taskFilter: {
          taskAssigned: true,
          taskCompletedRange: "day",
          taskCompletedAfterDate: null,
          taskCompletedByDate: null,
          taskTypeIds: [4],
        },
      } as Partial<ProspectFiltersType>),
    );
    saveSearch(
      SavedSearchPage.ProspectsGrid,
      ProspectGridSearchKey.GeneralQuestionnaireLast24Hrs,
      JSON.stringify({
        draftYears: [{ value: parseInt(year), label: year }],
        taskFilter: {
          taskAssigned: true,
          taskCompletedRange: "day",
          taskCompletedAfterDate: null,
          taskCompletedByDate: null,
          taskTypeIds: [1],
        },
      } as Partial<ProspectFiltersType>),
    );
    saveSearch(
      SavedSearchPage.ProspectsGrid,
      ProspectGridSearchKey.MedicalQuestionnaireLast24Hrs,
      JSON.stringify({
        draftYears: [{ value: parseInt(year), label: year }],
        taskFilter: {
          taskAssigned: true,
          taskCompletedRange: "day",
          taskCompletedAfterDate: null,
          taskCompletedByDate: null,
          taskTypeIds: [2],
        },
      } as Partial<ProspectFiltersType>),
    );
    saveSearch(
      SavedSearchPage.ProspectsGrid,
      ProspectGridSearchKey.ClubQuestionnaireLast24Hrs,
      JSON.stringify({
        draftYears: [{ value: parseInt(year), label: year }],
        taskFilter: {
          taskAssigned: true,
          taskCompletedRange: "day",
          taskCompletedAfterDate: null,
          taskCompletedByDate: null,
          taskTypeIds: [3],
        },
      } as Partial<ProspectFiltersType>),
    );
  };

  useEffect(() => {
    if (draftYear) {
      saveProspectGridSearches(draftYear);
    }
  }, [draftYear]);

  const getSearch = <P extends keyof SavedSearchPages>(
    page: P,
    searchKey: SavedSearchKeysFor<P>,
  ): SavedSearchValueFor<P> | null => {
    const existingSearchesStr = localStorage.getItem("savedSearches");
    if (existingSearchesStr === null) {
      return null;
    }
    const pages = JSON.parse(existingSearchesStr);
    if (!pages?.[page]?.[searchKey]) {
      return null;
    }
    return JSON.parse(pages[page][searchKey]) as SavedSearchValueFor<P>;
  };

  return <SavedSearchContext.Provider value={{ saveSearch, getSearch }}>{children}</SavedSearchContext.Provider>;
};

const useSavedSearches = (): SavedSearchContextType => {
  const context: SavedSearchContextType | undefined = useContext(SavedSearchContext);
  if (context === undefined) {
    throw new Error(`useSavedSearches must be used within a SavedSearchProvider`);
  }
  return context;
};

export { SavedSearchProvider, useSavedSearches };
